import dayjs from "dayjs";
import customParseFormat from "dayjs/plugin/customParseFormat";
import isSameOrAfter from "dayjs/plugin/isSameOrAfter";
import localizedFormat from "dayjs/plugin/localizedFormat";
import quarterOfYear from "dayjs/plugin/quarterOfYear";
import relativeTime from "dayjs/plugin/relativeTime";
import updateLocale from "dayjs/plugin/updateLocale";
import utc from "dayjs/plugin/utc";
import "dayjs/locale/es";

dayjs.extend(customParseFormat);
dayjs.extend(isSameOrAfter);
dayjs.extend(localizedFormat);
dayjs.extend(quarterOfYear);
dayjs.extend(relativeTime);
dayjs.extend(updateLocale);
dayjs.extend(utc);

// What a bad value formats as, worded as moment did rather than dayjs's "Invalid Date".
dayjs.updateLocale("en", { invalidDate: "Invalid date" });
dayjs.updateLocale("es", { invalidDate: "Fecha inválida" });

/** The date language for an i18n language; the app ships English and Spanish. */
export const dateLocaleFor = (language: string | undefined) =>
  language?.toLowerCase().startsWith("es") ? "es" : "en";

/** Formats dates in the reader's language. */
export const setDateLocale = (language: string | undefined) => {
  dayjs.locale(dateLocaleFor(language));
};

const US_DATE = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/;
const ISO =
  /^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2})(?::(\d{2})(?::(\d{2})(?:[.,](\d+))?)?)?)?(?:Z|[+-]\d{2}(?::?\d{2})?)?$/;

const isCalendarDay = (year: number, month: number, day: number) =>
  month >= 1 &&
  month <= 12 &&
  day >= 1 &&
  day <= new Date(Date.UTC(year, month, 0)).getUTCDate();

// ISO allows 24:00:00 for the end of a day, and moment read it as the next midnight.
const isClockTime = (
  hour: number,
  minute: number,
  second: number,
  fraction = "",
) =>
  (hour <= 23 && minute <= 59 && second <= 59) ||
  (hour === 24 && minute === 0 && second === 0 && !Number(fraction));

/**
 * A date stored either as ISO (its zone kept) or as a local M/D/YYYY, and
 * nothing else. An impossible day or time such as 02/31 or 25:00 is invalid,
 * as moment read it, where dayjs alone rolls it forward. Any other shape is
 * invalid on purpose: moment fell back to a forgiving parse that read
 * "Feb 5, 2026" as May 20.
 */
export const parseIsoOrUsDate = (value: string | number | Date) => {
  if (typeof value !== "string") return dayjs(value);
  const text = value.trim();
  const us = US_DATE.exec(text);
  if (us) {
    const [month, day, year] = [Number(us[1]), Number(us[2]), Number(us[3])];
    return isCalendarDay(year, month, day)
      ? dayjs(new Date(year, month - 1, day))
      : dayjs(null);
  }
  const iso = ISO.exec(text);
  if (iso) {
    const [year, month, day, hour, minute, second] = iso
      .slice(1, 7)
      .map((part) => Number(part ?? 0));
    return isCalendarDay(year, month, day) &&
      isClockTime(hour, minute, second, iso[7])
      ? dayjs(text)
      : dayjs(null);
  }
  return dayjs(null);
};

export type { Dayjs, ManipulateType } from "dayjs";
export default dayjs;
